import moment from "moment";
import { isOrdinaryEeaTier } from "../../sourcing-events/buyer/wizard/utils/karens";
import { eventIsDesignContest, isDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { entriesWithoutAssessment, juryProgress, juryReportConfirmed } from "../designContest/jury";
import { qualificationProgressKey } from "../qualification/bidSubject";
import {
  BidStatus,
  CompetitionStageDto,
  EFormType,
  EventRevisionState,
  NegotiationRoundStatus,
  ParticipationRequestStatus,
  PublicationChannel,
  PublicationStatus,
  SourcingEventStatus,
  StandstillExemption,
  type BuyerParticipationRequestDto,
  type BuyerParticipationRequestsDto,
  type EvalBidDto,
  type EvaluationDto,
  type NegotiationRoundDto,
  type ReceivedBidDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import {
  isQualificationScheme,
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../sourcing-events/buyer/wizard/utils/procedure";
import { timeLeftUntil, type TimeLeft } from "../../../common/formatting/timeLeft";
import { canSelectWinner, isBidDeadlineStillOpen, isEvaluationConcluded } from "../award/canSelectWinner";
import { hasStandingAwardDecision } from "../award/hasStandingAwardDecision";
import { everyScoreIn, isFullyScored, unscoredRoundTenders } from "../evaluation/evaluationCompleteness";
import { REVEAL_ANCHORS, subjectAnchor } from "../revealAnchor";
import { urls } from "../../../urls";
import { contractUrl } from "../contractUrl";
import type { EventTab } from "../EventTabs";
import type { EventSubTab } from "../useGoToEventTab";
import type { TranslationKey } from "../qualification/deriveQualificationSteps";
import {
  effectiveBidDeadline,
  effectiveRequestDeadline,
  isEvaluationLocked,
  publishedBidDeadline,
} from "../qualification/stage";
import { deriveMinimumRequirementReview } from "../minimum-requirements/deriveMinimumRequirementReview";
import { deriveTenderQualification } from "../qualification/tender/deriveTenderQualification";
import { openChangeDraft } from "../changes/changeScope";
import { changeWindowOf, isOpenForNewChange } from "../changes/changeWindow";
import { resultNoticePublication, resultNoticeStep } from "./resultNoticeState";
import { awardsWithoutPriorPublication } from "../../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";
import { hasDecisionOlderThanDelivery } from "../evaluation/staleScore";
import { allowsPartIIDialogue, dialoguePlanned, holdsDialogue, standingTendersAtStage } from "../rounds/partIIDialogue";
import { supportsNegotiationRounds } from "../rounds/supportsNegotiationRounds";
import { ORIGINAL_COMPETITION } from "../rounds/RoundSelectionContext";
import { announcedOnDoffin } from "../announcedOnDoffin";

// Every key this module can emit. They are looked up dynamically in NowPanel, so langen only sees
// them here:
// t('This procurement has been cancelled') t('The cancellation must be announced on Doffin') t('Announce the result')
// t('The cancellation must be announced on Doffin with a result notice')
// t('Reason for cancellation') t('This procurement is closed') t('Send the change notice')
// t('The change notice is sent – awaiting Doffin')
// t('The competition is open — deadline {{date}}') t('The competition is open')
// t('{{count}} requests received')
// t('{{count}} unanswered questions') t('Answer questions')
// t('{{count}} companies have registered interest')
// t('Start the qualification review') t('Continue the qualification review')
// t('Review the qualification of {{supplier}}')
// t('Assess the minimum requirements for {{supplier}}') t('Score {{supplier}}')
// t('The deadline has passed — ready to start the evaluation') t('Begin evaluation')
// t('The system is open for applications') t('The system has expired') t('{{count}} applications awaiting assessment')
// t('Shortest deadline: {{count}} working days (FOA § 26-6)') t('Assess applications')
// t('Every request is reviewed — choose who to invite') t('Go to the qualification')
// t('{{reviewed}} of {{total}} tenders assessed against the minimum requirements')
// t('Start the minimum requirements review') t('Continue the minimum requirements review')
// t('The evaluation is complete — ready for award')
// t('The evaluation is complete — awarding opens after the bid deadline {{date}}')
// t('Only the tenders from the latest round count towards the award')
// t('Tenderers that delivered nothing in the round stay in on their latest tender')
// t('Suppliers can still deliver or change their bids until then')
// t('Select a winner') t('Select the winners') t('Award to selected suppliers') t('Correct the standstill period')
// t('Choose at least one entry')
// t('Complete negotiation round {{round}} before awarding') t('Complete the round')
// t('Negotiation round {{round}} is open until {{date}}') t('Go to the round')
// t('{{reviewed}} of {{total}} tenders fully evaluated')
// t('Start the evaluation review') t('Continue the evaluation review')
// t('{{count}} tenders were delivered again after you assessed them', { count }) t('Check the new versions')
// t('A supplier delivered, changed or withdrew a tender after you completed the evaluation, so it was reopened')
// t('Ready to complete the evaluation') t('Complete the evaluation') t('Reopen the evaluation')
// t('Send the award letters to start the standstill period') t('Go to the award letters')
// t('Send the letters to all suppliers') t('Send the letters to the participants') t('Go to the letters')
// t('See the award')
// t('The standstill period has expired') t('Confirm award')
// t('Send the award letters before you confirm the award')
// t('No standstill period applies (FOA § 10-2 (3) b). You can now confirm the award.')
// t('No standstill period applies (FOA § 10-2 (3) c). You can now confirm the award.')
// t('The standstill period ends {{date}} ({{timeLeft}})')
// t('The award is confirmed') t('Create contract') t('Go to contract') t('{{done}} of {{total}} winners have a contract') t('No contract yet: {{suppliers}}')
// t('The award letters have not been sent yet') t('The letters to the participants have not been sent yet')
// t('The current bid deadline has passed — sending the change moves it to {{date}}')
// t('No tenders were received') t('No entries were received') t('Every tender has been rejected — nothing is left to evaluate')
// t('The bid deadline has passed, so it can no longer be extended')
// t('The deadline for entries has passed, so it can no longer be extended') t('Cancel the procurement')
// t('Extend the deadline') t('Continue the change') t('A change is waiting to be published')
// t('The contract award must be announced by {{date}} ({{timeLeft}})') t('Announce on Doffin')
// t('The contest\'s result must be announced by {{date}} ({{timeLeft}})')
// t('You can announce the result on Doffin (voluntary)') t('When was the contract concluded?')
// t('Enter the date') t('The result notice is sent – awaiting Doffin') t('The result is announced on Doffin')
// t('Send an intention notice to protect the contract, or conclude it now') t('Send the intention notice')
// t('You can send the intention notice while the standstill period runs')
// t('Conclude the contract without an intention notice') t('The intention notice is sent – awaiting Doffin')
// t('The contract can be concluded from {{date}} ({{timeLeft}})')
// t('Doffin refused the intention notice, so the contract is not protected yet') t('Send the intention notice again')
// t('See on Doffin') t('Do not announce')
// t('Hold dialogue') t('Award without dialogue') t('Continue the dialogue')
export const NOW_STATE_KEYS = [
  "SCHEME_INTAKE",
  "INTEREST_OPEN",
  "REQUESTS_OPEN",
  "TENDER_OPEN",
  "REQUEST_QUALIFICATION",
  "REQUEST_SELECTION",
  "TENDER_QUALIFICATION",
  "START_EVALUATION",
  "LOADING",
  "EVALUATION_UNAVAILABLE",
  "NO_TENDERS",
  "NO_QUALIFIED_TENDERS",
  "ROUND_OPEN",
  "MINIMUM_REQUIREMENTS",
  "EVALUATION",
  "ROUND_TO_COMPLETE",
  "READY_TO_AWARD",
  "JURY_REPORT_MISSING",
  "JURY_ASSESSMENT_MISSING",
  "JURY_REPORT_AWAITS_YOU",
  "JURY_REPORT_UNCONFIRMED",
  "JURY_DECISION_READY",
  "AWAITING_BID_DEADLINE",
  "AWARD_LETTERS_PENDING",
  "STANDSTILL_RUNNING",
  "STANDSTILL_EXPIRED",
  "AWARD_CONFIRMED",
  "INTENTION_NOTICE_OPTIONAL",
  "INTENTION_NOTICE_REFUSED",
  "INTENTION_NOTICE_SENT",
  "INTENTION_NOTICE_WAIT",
  "RESULT_NOTICE_CONCLUDED_DATE",
  "RESULT_NOTICE_DUE",
  "RESULT_NOTICE_VOLUNTARY",
  "RESULT_NOTICE_SENT",
  "RESULT_NOTICE_PUBLISHED",
  "CANCELLED",
  "CLOSED",
  "COMPLETED",
] as const;

export type NowStateKey = (typeof NOW_STATE_KEYS)[number];

export type NowCommandTarget =
  | {
    kind: "command";
    command:
      | "beginEvaluation"
      | "completeEvaluation"
      | "reopenEvaluation"
      | "awardEvent"
      | "confirmAward"
      | "sendAwardLetters"
      | "cancelProcurement"
      | "extendDeadline"
      | "continueChange"
      | "recordContractConcluded"
      | "announceResult"
      | "announceNoWinner"
      | "announceCancellation"
      | "declineResultNotice"
      | "sendIntentionNotice"
      | "concludeWithoutIntentionNotice"
      | "startDialogue"
      | "startDesignContestFollowUp"
      | "retryEvaluation";
  }
  | { kind: "command"; command: "completeRound"; roundId: string }
  | { kind: "command"; command: "confirmJuryReport"; memberIndex: number; reportWrittenAt: string }
  | { kind: "command"; command: "createContract"; bidId: string };

export type NowActionTarget =
  | { kind: "tab"; tab: EventTab; subTab?: EventSubTab; focusBidId?: string; reveal?: string; round?: string }
  | { kind: "url"; url: ReturnType<typeof contractUrl> }
  | { kind: "doffinNotice"; externalId: string }
  | NowCommandTarget;

export interface NowAction {
  labelKey: string;
  labelParams?: Record<string, string | number>;
  icon?: "send";
  tone?: "danger";
  disabled?: boolean;
  target: NowActionTarget;
}

export interface NowProgress {
  done: number;
  total: number;
}

export interface NowPanelState {
  key: NowStateKey;
  status: TranslationKey & { dateIso?: string; dayIso?: string; timeLeft?: TimeLeft };
  detail: (TranslationKey & { text?: string })[];
  progress: NowProgress | null;
  action: NowAction | null;
  secondary?: NowAction | null;
  alternatives?: NowAction[];
}

export interface NowPanelInput {
  event: SourcingEventDto;
  now: number;
  unansweredQuestions: number;
  participationRequests: BuyerParticipationRequestsDto | null;
  bids: ReceivedBidDto[];
  bidsLoaded: boolean;
  selectedWinnerCount: number;
  evaluation: EvaluationDto | null;
  evaluationLoaded: boolean;
  evaluationFailed: boolean;
  rounds: readonly NegotiationRoundDto[];
  awardLettersSent: boolean | null;
  awardLettersApproved: boolean | null;
  activeTab: EventTab;
  focusModeEnabled: boolean;
  mayCreateContract: boolean;
  mayManage: boolean;
  meId?: string | null;
  standstillChoiceIncomplete?: boolean;
}

const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Draft,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const tab = (target: EventTab): NowActionTarget => ({ kind: "tab", tab: target });

const seeTheAward: NowAction = { labelKey: "See the award", target: tab("award") };

const TERMINAL_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
]);

/** False where the panel renders nothing or a terminal line, so callers can skip fetching bids. */
export const panelReadsBids = (status: SourcingEventStatus): boolean =>
  !HIDDEN_STATUSES.has(status) && !TERMINAL_STATUSES.has(status);

const publicProcedureOf = (event: SourcingEventDto) =>
  event.eventContext.type === "Public" ? event.eventContext.procedureType : null;

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

const registeredInterest = (event: SourcingEventDto): TranslationKey[] => {
  const count = (event.supplierRegistrations ?? []).length;
  return count > 0 ? [{ key: "{{count}} companies have registered interest", params: { count } }] : [];
};

const unansweredDetail = (count: number): TranslationKey[] =>
  count > 0 ? [{ key: "{{count}} unanswered questions", params: { count } }] : [];

const answerQuestions = (count: number): NowAction | null =>
  count > 0
    ? { labelKey: "Answer questions", target: { kind: "tab", tab: "communication", subTab: "questions" } }
    : null;

const openCompetition = (
  key: NowStateKey,
  deadlineIso: string | null,
  detail: TranslationKey[],
  action: NowAction | null,
): NowPanelState => ({
  key,
  status: deadlineIso
    ? { key: "The competition is open — deadline {{date}}", dateIso: deadlineIso }
    : { key: "The competition is open" },
  detail,
  progress: null,
  action,
});

const resumeLabel = (progress: NowProgress, startKey: string, continueKey: string): string =>
  progress.done === 0 ? startKey : continueKey;

interface ReviewLabels {
  startKey: string;
  continueKey: string;
  focusKey: string;
}

interface ReviewSubject {
  bidId: string;
  supplierName: string;
}

const reviewAction = (
  input: NowPanelInput,
  tab: EventTab,
  progress: NowProgress,
  labels: ReviewLabels,
  next: ReviewSubject | undefined,
  round?: string,
): NowAction => {
  const namesNext = input.activeTab === tab && next != null;
  if (!namesNext) {
    return {
      labelKey: resumeLabel(progress, labels.startKey, labels.continueKey),
      target: { kind: "tab", tab, round },
    };
  }
  if (!input.focusModeEnabled) {
    return {
      labelKey: resumeLabel(progress, labels.startKey, labels.continueKey),
      target: { kind: "tab", tab, reveal: subjectAnchor(next.bidId), round },
    };
  }
  return {
    labelKey: labels.focusKey,
    labelParams: { supplier: next.supplierName },
    target: { kind: "tab", tab, focusBidId: next.bidId, round },
  };
};

const reviewedRoundParam = (rounds: readonly NegotiationRoundDto[]): string | undefined =>
  rounds.length > 0 ? latestRoundOf(rounds)?.id ?? ORIGINAL_COMPETITION : undefined;

export const latestRoundOf = (rounds: readonly NegotiationRoundDto[]): NegotiationRoundDto | null =>
  [...rounds]
    .filter((round) => round.status !== NegotiationRoundStatus.Draft)
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1) ?? null;

const tendersOf = (bids: ReceivedBidDto[], roundId: string | null): ReceivedBidDto[] =>
  bids.filter((bid) => (bid.forNegotiationRound ?? null) === roundId);

const cancelled = (event: SourcingEventDto, now: number): NowPanelState => {
  const viaChangeNotice = cancellationByChangeNotice(event, now);
  if (viaChangeNotice) return viaChangeNotice;
  const reason = event.cancellation?.reason?.trim();
  const publication = resultNoticePublication(event);
  const pending = publication?.status === PublicationStatus.Pending;
  const awaitsResultNotice = publication == null && announcedOnDoffin(event);
  return {
    ...terminal(
      "CANCELLED",
      awaitsResultNotice
        ? "The cancellation must be announced on Doffin with a result notice"
        : "This procurement has been cancelled",
    ),
    detail: [
      ...(reason ? [{ key: "Reason for cancellation", text: reason }] : []),
      ...(pending ? [{ key: "The result notice is sent – awaiting Doffin" }] : []),
    ],
    action: noWinnerNoticeAction(event),
  };
};

const cancellationByChangeNotice = (event: SourcingEventDto, now: number): NowPanelState | null => {
  const cancellation = event.cancellation;
  if (cancellation == null || cancellation.nonAwardReason != null || !announcedOnDoffin(event)) return null;
  if (resultNoticePublication(event) != null) return null;
  const reason = cancellation.reason?.trim();
  const reasonDetail = reason ? [{ key: "Reason for cancellation", text: reason }] : [];
  const notice = cancellation.changeNotice;
  if (notice?.status === PublicationStatus.Published) {
    return {
      ...terminal("CANCELLED", "This procurement has been cancelled"),
      detail: reasonDetail,
      action: notice.externalId
        ? { labelKey: "See on Doffin", target: { kind: "doffinNotice", externalId: notice.externalId } }
        : null,
    };
  }
  if (notice?.status === PublicationStatus.Pending) {
    return {
      ...terminal("CANCELLED", "This procurement has been cancelled"),
      detail: [...reasonDetail, { key: "The change notice is sent – awaiting Doffin" }],
    };
  }
  const deadline = event.announcedBidSubmissionDeadline ?? event.milestoneDates.bidSubmissionDeadline;
  if (deadline == null || hasPassed(deadline, now)) return null;
  return {
    ...terminal("CANCELLED", "The cancellation must be announced on Doffin"),
    detail: reasonDetail,
    action: {
      labelKey: "Send the change notice",
      icon: "send",
      target: { kind: "command", command: "announceCancellation" },
    },
  };
};

const noWinnerNoticeAction = (event: SourcingEventDto): NowAction | null => {
  const publication = resultNoticePublication(event);
  if (publication?.status === PublicationStatus.Published) {
    return publication.externalId
      ? { labelKey: "See on Doffin", target: { kind: "doffinNotice", externalId: publication.externalId } }
      : null;
  }
  if (publication != null || !announcedOnDoffin(event)) return null;
  return { labelKey: "Announce the result", icon: "send", target: { kind: "command", command: "announceNoWinner" } };
};

export const deriveNowPanel = (input: NowPanelInput): NowPanelState | null => {
  const state = offerPartIIDialogue(input, deriveState(input));
  const keepsItsAction = state?.key === "EVALUATION_UNAVAILABLE" || state?.key === "JURY_REPORT_AWAITS_YOU";
  if (state == null || input.mayManage || keepsItsAction) return state;
  return { ...state, action: null, secondary: null, alternatives: [] };
};

const holdDialogue: NowAction = {
  labelKey: "Hold dialogue",
  target: { kind: "command", command: "startDialogue" },
};

const offerPartIIDialogue = (input: NowPanelInput, state: NowPanelState | null): NowPanelState | null => {
  const { event, rounds, now } = input;
  if (state?.key !== "READY_TO_AWARD" || holdsDialogue(rounds)) return state;
  if (!allowsPartIIDialogue(event) || !supportsNegotiationRounds(event, now)) return state;
  const awardsDirectly = state.action?.target.kind === "command" && state.action.target.command === "awardEvent";
  const award = awardsDirectly && state.action ? { ...state.action, labelKey: "Award without dialogue" } : state.action;
  if (dialoguePlanned(event)) return { ...state, action: holdDialogue, secondary: award };
  return { ...state, action: award, secondary: holdDialogue };
};

const deriveState = (input: NowPanelInput): NowPanelState | null => {
  const { event } = input;
  if (HIDDEN_STATUSES.has(event.status)) return null;

  switch (event.status) {
    case SourcingEventStatus.Cancelled:
      return cancelled(event, input.now);
    case SourcingEventStatus.Closed:
      return terminal("CLOSED", "This procurement is closed");
    case SourcingEventStatus.Completed:
      return unmetResultNotice(input) ?? terminal("COMPLETED", "This procurement is completed");
    case SourcingEventStatus.Published:
      return published(input);
    case SourcingEventStatus.Evaluation:
    case SourcingEventStatus.Evaluated:
      return evaluating(input);
    case SourcingEventStatus.AwardDecision:
      return awardDecision(input);
    case SourcingEventStatus.Awarded:
      return awardConfirmed(input);
    default:
      return null;
  }
};

const terminal = (key: NowStateKey, statusKey: string): NowPanelState => ({
  key,
  status: { key: statusKey },
  detail: [],
  progress: null,
  action: null,
});

const published = (input: NowPanelInput): NowPanelState | null => {
  const { event, now, unansweredQuestions, participationRequests, bids } = input;
  const procedureType = publicProcedureOf(event);

  if (isSchemeProcedure(procedureType)) return schemeIntake(input);

  if (isTwoStageProcedure(procedureType)) {
    switch (event.competitionStage) {
      case CompetitionStageDto.InterestOpen:
        return interestOpen(event);
      case CompetitionStageDto.RequestsOpen:
        return openCompetition(
          "REQUESTS_OPEN",
          effectiveRequestDeadline(event),
          [
            { key: "{{count}} requests received", params: { count: participationRequests?.count ?? 0 } },
            ...unansweredDetail(unansweredQuestions),
          ],
          answerQuestions(unansweredQuestions),
        );
      case CompetitionStageDto.Qualification:
        return requestQualification(input);
      default:
        break;
    }
  }

  const announcedDeadline = publishedBidDeadline(event);
  if (!hasPassed(announcedDeadline, now)) {
    return openCompetition(
      "TENDER_OPEN",
      announcedDeadline,
      [...registeredInterest(event), ...unansweredDetail(unansweredQuestions)],
      answerQuestions(unansweredQuestions),
    );
  }

  if (!hasPassed(effectiveBidDeadline(event), now)) {
    return {
      key: "TENDER_OPEN",
      status: {
        key: "The current bid deadline has passed — sending the change moves it to {{date}}",
        dateIso: effectiveBidDeadline(event) ?? undefined,
      },
      detail: unansweredDetail(unansweredQuestions),
      progress: null,
      action: answerQuestions(unansweredQuestions),
    };
  }

  if (isEvaluationLocked(event)) {
    const summary = deriveTenderQualification(event.eventData.selectionCriteria ?? [], tendersOf(bids, null));
    if (input.bidsLoaded && summary.rows.length === 0) return deadEnd(input, "NO_TENDERS", nothingReceivedKey(event));
    const progress = { done: summary.qualified + summary.rejected, total: summary.rows.length };
    return {
      key: "TENDER_QUALIFICATION",
      status: {
        key: qualificationProgressKey(event),
        params: { resolved: progress.done, total: progress.total },
      },
      detail: [],
      progress,
      action: reviewAction(
        input,
        "qualification",
        progress,
        {
          startKey: "Start the qualification review",
          continueKey: "Continue the qualification review",
          focusKey: "Review the qualification of {{supplier}}",
        },
        summary.rows.find((row) => row.state === "PENDING"),
      ),
    };
  }

  const openRound = latestOpenRound(input.rounds);
  if (openRound && !hasPassed(openRound.deadline, now)) return roundOpen(openRound);

  return startEvaluation(input);
};

const roundOpen = (round: NegotiationRoundDto): NowPanelState => ({
  key: "ROUND_OPEN",
  status: {
    key: "Negotiation round {{round}} is open until {{date}}",
    params: { round: round.roundNumber },
    dateIso: round.deadline,
  },
  detail: [],
  progress: null,
  action: { labelKey: "Go to the round", target: tab("request") },
});

// BeginEvaluation.kt lets any published event past its deadline through, deliveries or not.
const startEvaluation = (input: NowPanelInput): NowPanelState => {
  if (input.bidsLoaded && input.bids.length === 0) return deadEnd(input, "NO_TENDERS", nothingReceivedKey(input.event));
  return {
    key: "START_EVALUATION",
    status: { key: "The deadline has passed — ready to start the evaluation" },
    detail: [],
    progress: null,
    action: { labelKey: "Begin evaluation", target: { kind: "command", command: "beginEvaluation" } },
  };
};

const cancelProcurement: NowAction = {
  labelKey: "Cancel the procurement",
  tone: "danger",
  target: { kind: "command", command: "cancelProcurement" },
};

const nothingReceivedKey = (event: SourcingEventDto): string =>
  eventIsDesignContest(event) ? "No entries were received" : "No tenders were received";

const deadEnd = (input: NowPanelInput, key: NowStateKey, statusKey: string): NowPanelState => {
  const { event, now } = input;
  const way = wayOutOf(event, now);
  const state: NowPanelState = {
    key,
    status: { key: statusKey },
    detail: way.detail ? [{ key: way.detail }] : [],
    progress: null,
    action: null,
    secondary: null,
  };
  if (way.forward == null) return { ...state, action: cancelProcurement };
  return { ...state, action: way.forward, secondary: cancelProcurement };
};

const deadlinePassedKey = (event: SourcingEventDto): string =>
  eventIsDesignContest(event) ?
    "The deadline for entries has passed, so it can no longer be extended"
  : "The bid deadline has passed, so it can no longer be extended";

const wayOutOf = (
  event: SourcingEventDto,
  now: number,
): { forward: NowAction | null; detail: string | null } => {
  if (changeWindowOf(event, now) === "closed") {
    return { forward: null, detail: deadlinePassedKey(event) };
  }
  if (openChangeDraft(event)) {
    return { forward: { labelKey: "Continue the change", target: { kind: "command", command: "continueChange" } }, detail: null };
  }
  if ((event.changes ?? []).some((change) => change.state === EventRevisionState.PublicationPending)) {
    return { forward: null, detail: "A change is waiting to be published" };
  }
  if (isOpenForNewChange(event, now)) {
    return { forward: { labelKey: "Extend the deadline", target: { kind: "command", command: "extendDeadline" } }, detail: null };
  }
  return { forward: null, detail: deadlinePassedKey(event) };
};

const schemeValidTo = (event: SourcingEventDto): string | null => {
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  return publicContext?.dpsScheme?.validTo ?? publicContext?.qualificationScheme?.validTo ?? null;
};

// Forsyningsforskriften § 13-2 clocks admission in calendar months, not in virkedager.
const shortestAssessmentWindow = (
  event: SourcingEventDto,
  awaiting: BuyerParticipationRequestDto[],
): number | null =>
  isQualificationScheme(publicProcedureOf(event))
    ? null
    : awaiting.reduce<number | null>((fewest, request) => {
      const remaining = request.assessmentWorkingDaysRemaining;
      if (remaining == null) return fewest;
      return fewest == null || remaining < fewest ? remaining : fewest;
    }, null);

const schemeIntake = (input: NowPanelInput): NowPanelState => {
  const { event, now, participationRequests } = input;
  const awaiting = (participationRequests?.requests ?? []).filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  );

  if (awaiting.length === 0) {
    const expired = hasPassed(schemeValidTo(event), now);
    return {
      key: "SCHEME_INTAKE",
      status: { key: expired ? "The system has expired" : "The system is open for applications" },
      detail: [],
      progress: null,
      action: null,
    };
  }

  const shortest = shortestAssessmentWindow(event, awaiting);
  return {
    key: "SCHEME_INTAKE",
    status: { key: "{{count}} applications awaiting assessment", params: { count: awaiting.length } },
    detail:
      shortest == null
        ? []
        : [{ key: "Shortest deadline: {{count}} working days (FOA § 26-6)", params: { count: shortest } }],
    progress: null,
    action: {
      labelKey: "Assess applications",
      target: { kind: "tab", tab: "dpsSuppliers", reveal: REVEAL_ANCHORS.dpsApplications },
    },
  };
};

const interestOpen = (event: SourcingEventDto): NowPanelState => ({
  key: "INTEREST_OPEN",
  status: {
    key: "{{count}} companies have registered interest",
    params: { count: (event.supplierRegistrations ?? []).length },
  },
  detail: [],
  progress: null,
  action: {
    labelKey: "Start from the prior information notice",
    target: { kind: "tab", tab: "qualification" },
  },
});

const requestQualification = (input: NowPanelInput): NowPanelState => {
  const requests = input.participationRequests?.requests ?? [];
  const unresolved = requests.filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  ).length;
  const progress = { done: requests.length - unresolved, total: requests.length };

  if (unresolved === 0 && requests.length > 0) {
    return {
      key: "REQUEST_SELECTION",
      status: { key: "Every request is reviewed — choose who to invite" },
      detail: [],
      progress: null,
      action: {
        labelKey: "Go to the qualification",
        target: { kind: "tab", tab: "qualification", reveal: REVEAL_ANCHORS.invitationSelection },
      },
    };
  }

  return {
    key: "REQUEST_QUALIFICATION",
    status: {
      key: qualificationProgressKey(input.event),
      params: { resolved: progress.done, total: progress.total },
    },
    detail: [],
    progress,
    action: {
      labelKey: resumeLabel(progress, "Start the qualification review", "Continue the qualification review"),
      target: { kind: "tab", tab: "qualification", reveal: REVEAL_ANCHORS.qualificationReview },
    },
  };
};

// canSelectWinner lets an EVALUATED event through with a round still open; AwardSourcingEvent.kt
// rejects that with CannotAwardWithOpenNegotiationRound.
const awardAction = (input: NowPanelInput): NowAction | null => {
  const { event, rounds, selectedWinnerCount, now } = input;
  const openRound = rounds.some((round) => round.status === NegotiationRoundStatus.Published);
  if (selectedWinnerCount === 0 || input.standstillChoiceIncomplete) return null;
  if (openRound || !canSelectWinner(event, rounds, now)) return null;
  const labelKey = !eventIsDesignContest(event)
    ? "Award to selected suppliers"
    : selectedWinnerCount === 1
      ? "Select a winner"
      : "Select the winners";
  return { labelKey, target: { kind: "command", command: "awardEvent" } };
};

const loading: NowPanelState = {
  key: "LOADING",
  status: { key: "Loading" },
  detail: [],
  progress: null,
  action: null,
};

const evaluationUnavailable: NowPanelState = {
  key: "EVALUATION_UNAVAILABLE",
  status: { key: "Could not load the evaluation." },
  detail: [],
  progress: null,
  secondary: null,
  action: { labelKey: "Try again", target: { kind: "command", command: "retryEvaluation" } },
};

// t('The jury assesses the entries anonymously') t('Write the jury\'s report')
// t('{{done}} of {{total}} jury members have confirmed the report') t('See the jury\'s report')
// t('The whole jury has confirmed its report') t('Register the jury\'s decision')
// t('The entries stay anonymous until the jury\'s decision is registered')
// t('Ranked entries without the jury\'s assessment: {{count}}')
// t('The jury\'s report awaits your confirmation') t('Confirm the report')
const designContestJury = (event: SourcingEventDto, meId?: string | null): NowPanelState | null => {
  const ctx = event.eventContext.type === "Public" ? event.eventContext : null;
  if (!isDesignContest(ctx?.procedureType) || event.status !== SourcingEventStatus.Evaluation) return null;
  const contest = ctx?.designContest;
  const toJury: NowAction = { labelKey: "See the jury's report", target: { kind: "tab", tab: "evaluation" } };
  const anonymity = event.entriesAnonymous
    ? [{ key: "The entries stay anonymous until the jury's decision is registered" }]
    : [];
  if (!contest?.juryReport) {
    return {
      key: "JURY_REPORT_MISSING",
      status: { key: "The jury assesses the entries anonymously" },
      detail: anonymity,
      progress: null,
      action: { labelKey: "Write the jury's report", target: { kind: "tab", tab: "evaluation" } },
    };
  }
  const unassessed = entriesWithoutAssessment(contest.juryReport).length;
  if (unassessed > 0) {
    return {
      key: "JURY_ASSESSMENT_MISSING",
      status: { key: "Ranked entries without the jury's assessment: {{count}}", params: { count: unassessed } },
      detail: anonymity,
      progress: null,
      action: toJury,
    };
  }
  const myUnconfirmedSeat = (contest.jury ?? []).findIndex(
    (member, index) =>
      meId != null &&
      member.personId === meId &&
      !(contest.juryConfirmations ?? []).some((confirmation) => confirmation.memberIndex === index),
  );
  if (myUnconfirmedSeat >= 0) {
    return {
      key: "JURY_REPORT_AWAITS_YOU",
      status: { key: "The jury's report awaits your confirmation" },
      detail: anonymity,
      progress: juryProgress(contest),
      secondary: toJury,
      action: {
        labelKey: "Confirm the report",
        target: {
          kind: "command",
          command: "confirmJuryReport",
          memberIndex: myUnconfirmedSeat,
          reportWrittenAt: contest.juryReport.writtenAt,
        },
      },
    };
  }
  if (!juryReportConfirmed(contest)) {
    const progress = juryProgress(contest);
    return {
      key: "JURY_REPORT_UNCONFIRMED",
      status: { key: "{{done}} of {{total}} jury members have confirmed the report", params: progress },
      detail: anonymity,
      progress,
      action: toJury,
    };
  }
  return {
    key: "JURY_DECISION_READY",
    status: { key: "The whole jury has confirmed its report" },
    detail: anonymity,
    progress: null,
    secondary: toJury,
    action: { labelKey: "Register the jury's decision", target: { kind: "command", command: "completeEvaluation" } },
  };
};

const evaluating = (input: NowPanelInput): NowPanelState => {
  const { event, evaluation, rounds } = input;

  if (hasStandingAwardDecision(event)) return awardConfirmed(input);
  const juryWork = designContestJury(event, input.meId);
  if (juryWork) return juryWork;
  if (input.evaluationFailed) return evaluationUnavailable;
  if (!input.evaluationLoaded) return loading;

  const minimumRequirements = minimumRequirementsReview(input);
  if (minimumRequirements != null) {
    const { progress } = minimumRequirements;
    return {
      key: "MINIMUM_REQUIREMENTS",
      status: {
        key: "{{reviewed}} of {{total}} tenders assessed against the minimum requirements",
        params: { reviewed: progress.done, total: progress.total },
      },
      detail: [],
      progress,
      secondary: null,
      action: reviewAction(
        input,
        "minimumRequirements",
        progress,
        {
          startKey: "Start the minimum requirements review",
          continueKey: "Continue the minimum requirements review",
          focusKey: "Assess the minimum requirements for {{supplier}}",
        },
        minimumRequirements.next,
        reviewedRoundParam(rounds),
      ),
    };
  }

  const rankable = (evaluation?.bids ?? []).filter((bid) => bid.status !== BidStatus.Rejected);
  const progress = { done: rankable.filter(isFullyScored).length, total: rankable.length };
  const roundTenderUnscored = unscoredRoundTenders(evaluation, rounds).length > 0;
  const nothingDelivered = (evaluation?.bids ?? []).length === 0;
  const everyTenderRejected =
    !nothingDelivered && (evaluation?.bids ?? []).every((bid) => bid.status === BidStatus.Rejected);

  const openRound = latestOpenRound(rounds);
  if (
    openRound &&
    (event.status === SourcingEventStatus.Evaluated || everyScoreIn(evaluation) || nothingDelivered)
  ) {
    return roundToComplete(openRound, input.now);
  }

  if (everyTenderRejected && openRound == null) {
    return deadEnd(input, "NO_QUALIFIED_TENDERS", "Every tender has been rejected — nothing is left to evaluate");
  }

  const reopenEvaluation: NowAction | null =
    event.status === SourcingEventStatus.Evaluated
      ? { labelKey: "Reopen the evaluation", target: { kind: "command", command: "reopenEvaluation" } }
      : null;

  const continueDialogue = draftRoundAction(event, rounds);
  const besideTheMainAction =
    continueDialogue == null
      ? { secondary: reopenEvaluation }
      : { secondary: continueDialogue, alternatives: reopenEvaluation == null ? [] : [reopenEvaluation] };

  if (isEvaluationConcluded(event.status, rounds) && isBidDeadlineStillOpen(event, input.now)) {
    return {
      key: "AWAITING_BID_DEADLINE",
      status: {
        key: "The evaluation is complete — awarding opens after the bid deadline {{date}}",
        dateIso: effectiveBidDeadline(event) ?? undefined,
      },
      detail: [{ key: "Suppliers can still deliver or change their bids until then" }],
      progress: null,
      ...besideTheMainAction,
      action: null,
    };
  }

  if (canSelectWinner(event, rounds, input.now) && !roundTenderUnscored) {
    return {
      key: "READY_TO_AWARD",
      status: { key: "The evaluation is complete — ready for award" },
      detail: [],
      progress: null,
      ...besideTheMainAction,
      action:
        awardAction(input) ??
        (input.selectedWinnerCount > 0 && input.standstillChoiceIncomplete
          ? {
              labelKey: "Correct the standstill period",
              target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.standstill },
            }
          : eventIsDesignContest(event)
            ? {
                labelKey: "Choose at least one entry",
                disabled: true,
                target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.winnerSelection },
              }
            : {
                labelKey: "Select a winner",
                target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.winnerSelection },
              }),
    };
  }

  const fullyEvaluatedStatus = {
    key: "{{reviewed}} of {{total}} tenders fully evaluated",
    params: { reviewed: progress.done, total: progress.total },
  };
  const latestRoundOnlyDetail = !roundTenderUnscored
    ? []
    : allowsPartIIDialogue(event)
      ? [{ key: "Tenderers that delivered nothing in the round stay in on their latest tender" }]
      : [{ key: "Only the tenders from the latest round count towards the award" }];

  if (roundTenderUnscored && event.status === SourcingEventStatus.Evaluated) {
    return {
      key: "EVALUATION",
      status: fullyEvaluatedStatus,
      detail: latestRoundOnlyDetail,
      progress,
      secondary: null,
      action: reopenEvaluation,
    };
  }

  const reopenedDetail = event.evaluationReopenedBySupplier
    ? [{ key: "A supplier delivered, changed or withdrew a tender after you completed the evaluation, so it was reopened" }]
    : [];
  const deliveredAgain = rankable.filter(hasDecisionOlderThanDelivery);

  if (everyScoreIn(evaluation) && latestRoundOf(rounds) == null && deliveredAgain.length > 0) {
    return {
      key: "EVALUATION",
      status: {
        key: "{{count}} tenders were delivered again after you assessed them",
        params: { count: deliveredAgain.length },
      },
      detail: reopenedDetail,
      progress: null,
      action: {
        labelKey: "Check the new versions",
        target: { kind: "tab", tab: "evaluation", reveal: subjectAnchor(deliveredAgain[0].bidId) },
      },
    };
  }

  if (everyScoreIn(evaluation) && latestRoundOf(rounds) == null) {
    return {
      key: "EVALUATION",
      status: { key: "Ready to complete the evaluation" },
      detail: reopenedDetail,
      progress: null,
      ...dialogueFirst(
        { labelKey: "Complete the evaluation", target: { kind: "command", command: "completeEvaluation" } },
        continueDialogue,
      ),
    };
  }

  if (nothingDelivered) {
    return {
      key: "EVALUATION",
      status: { key: "Nothing to evaluate yet" },
      detail: [],
      progress: null,
      action: null,
    };
  }

  return {
    key: "EVALUATION",
    status: fullyEvaluatedStatus,
    detail: [...reopenedDetail, ...latestRoundOnlyDetail],
    progress: progress.total > 0 ? progress : null,
    secondary: continueDialogue,
    action: reviewAction(
      input,
      "evaluation",
      progress,
      {
        startKey: "Start the evaluation review",
        continueKey: "Continue the evaluation review",
        focusKey: "Score {{supplier}}",
      },
      // A price-ranked competition scores nothing by hand, so every bid reads as fully evaluated.
      nextToScore(rankable),
      reviewedRoundParam(rounds),
    ),
  };
};

const dialogueFirst = (
  completeEvaluation: NowAction,
  continueDialogue: NowAction | null,
): Pick<NowPanelState, "action" | "secondary"> =>
  continueDialogue
    ? { action: continueDialogue, secondary: completeEvaluation }
    : { action: completeEvaluation, secondary: null };

const draftRoundAction = (event: SourcingEventDto, rounds: readonly NegotiationRoundDto[]): NowAction | null => {
  const draft = rounds.find((round) => round.status === NegotiationRoundStatus.Draft);
  if (draft == null) return null;
  if (event.status === SourcingEventStatus.Evaluated) {
    return { labelKey: "Continue the dialogue", target: { kind: "command", command: "startDialogue" } };
  }
  return {
    labelKey: "Continue the dialogue",
    target: { kind: "url", url: urls.app.sourcingEvents.editNegotiationRound.go(event.id, draft.id) },
  };
};

const latestOpenRound = (rounds: readonly NegotiationRoundDto[]): NegotiationRoundDto | null =>
  [...rounds]
    .filter((round) => round.status === NegotiationRoundStatus.Published)
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1) ?? null;

const roundToComplete = (round: NegotiationRoundDto, now: number): NowPanelState =>
  hasPassed(round.deadline, now)
    ? {
        key: "ROUND_TO_COMPLETE",
        status: { key: "Complete negotiation round {{round}} before awarding", params: { round: round.roundNumber } },
        detail: [],
        progress: null,
        action: { labelKey: "Complete the round", target: { kind: "command", command: "completeRound", roundId: round.id } },
      }
    : {
        key: "ROUND_TO_COMPLETE",
        status: {
          key: "Negotiation round {{round}} is open until {{date}}",
          params: { round: round.roundNumber },
          dateIso: round.deadline,
        },
        detail: [],
        progress: null,
        action: { labelKey: "Go to the round", target: tab("request") },
      };

const nextToScore = (rankable: EvalBidDto[]): ReviewSubject | undefined => {
  const bid = rankable.find((candidate) => !isFullyScored(candidate));
  return bid && { bidId: bid.bidId, supplierName: bid.supplierName };
};

const minimumRequirementsReview = (
  input: NowPanelInput,
): { progress: NowProgress; next: ReviewSubject | undefined } | null => {
  const requirements = input.event.eventData.minimumRequirements ?? [];
  if (requirements.length === 0) return null;
  const latestRound = latestRoundOf(input.rounds);
  const tenders =
    latestRound && allowsPartIIDialogue(input.event)
      ? standingTendersAtStage(input.bids, input.rounds, latestRound)
      : tendersOf(input.bids, latestRound?.id ?? null);
  const summary = deriveMinimumRequirementReview(requirements, tenders);
  if (summary.rows.length === 0 || summary.isResolved) return null;
  return {
    progress: { done: summary.assessed, total: summary.assessable },
    next: summary.rows.find((row) => row.state === "PENDING"),
  };
};

const awardLettersAction = (input: NowPanelInput): NowAction =>
  input.awardLettersApproved === true
    ? {
      labelKey: eventIsDesignContest(input.event)
        ? "Send the letters to the participants"
        : "Send the letters to all suppliers",
      icon: "send",
      target: { kind: "command", command: "sendAwardLetters" },
    }
    : {
      labelKey: eventIsDesignContest(input.event) ? "Go to the letters" : "Go to the award letters",
      target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.awardLetters },
    };

export const NO_STANDSTILL_STATUS: Record<StandstillExemption, string> = {
  [StandstillExemption.SoleConcernedSupplier]:
    "No standstill period applies (FOA § 10-2 (3) b). You can now confirm the award.",
  [StandstillExemption.FrameworkAgreementCallOff]:
    "No standstill period applies (FOA § 10-2 (3) c). You can now confirm the award.",
};

const awardDecision = (input: NowPanelInput): NowPanelState => {
  const expiresAt = input.event.award?.standstillExpiresAt;
  const exemption = input.event.award?.standstillExemption;

  if (expiresAt == null) {
    return {
      key: "AWARD_LETTERS_PENDING",
      status: {
        key: exemption
          ? "Send the award letters before you confirm the award"
          : "Send the award letters to start the standstill period",
      },
      detail: [],
      progress: null,
      action: awardLettersAction(input),
    };
  }

  if (hasPassed(expiresAt, input.now)) {
    return {
      key: "STANDSTILL_EXPIRED",
      status: { key: exemption ? NO_STANDSTILL_STATUS[exemption] : "The standstill period has expired" },
      detail: [],
      progress: null,
      action: { labelKey: "Confirm award", target: { kind: "command", command: "confirmAward" } },
    };
  }

  return {
    key: "STANDSTILL_RUNNING",
    status: {
      key: "The standstill period ends {{date}} ({{timeLeft}})",
      dateIso: expiresAt,
      timeLeft: timeLeftUntil(expiresAt, moment(input.now)),
    },
    detail: [],
    progress: null,
    action: seeTheAward,
    ...intentionNoticeDuringStandstill(input.event),
  };
};

// FOA § 8-18: below the EEA tier an intention notice is optional, and none is offered there.
const offersIntentionNotice = (event: SourcingEventDto): boolean => {
  const ctx = event.eventContext.type === "Public" ? event.eventContext : null;
  return awardsWithoutPriorPublication(ctx?.procedureType) && isOrdinaryEeaTier(ctx?.regulation, ctx?.foaPart);
};

const intentionNoticeDuringStandstill = (event: SourcingEventDto): Partial<NowPanelState> => {
  if (!offersIntentionNotice(event)) return {};
  const notice = event.award?.intentionNotice;
  if (notice == null) {
    return {
      action: latestIntentionNoticeFailed(event) ? sendIntentionNoticeAgain : sendIntentionNotice,
      secondary: seeTheAward,
      detail: [
        latestIntentionNoticeFailed(event)
          ? { key: "Doffin refused the intention notice, so the contract is not protected yet" }
          : { key: "You can send the intention notice while the standstill period runs" },
      ],
    };
  }
  if (notice.status !== PublicationStatus.Published || notice.noticeNumber == null) {
    return { detail: [{ key: "The intention notice is sent – awaiting Doffin" }] };
  }
  return {
    secondary: { labelKey: "See on Doffin", target: { kind: "doffinNotice", externalId: notice.noticeNumber } },
  };
};

const winningBids = (event: SourcingEventDto, bids: ReceivedBidDto[]): ReceivedBidDto[] =>
  (event.award?.winningBidIds ?? []).flatMap((id) => bids.filter((bid) => bid.id === id));

const createContractFor = (bid: ReceivedBidDto): NowAction => ({
  labelKey: "Create contract",
  target: { kind: "command", command: "createContract", bidId: bid.id },
});

const awardConfirmedAction = (
  event: SourcingEventDto,
  bids: ReceivedBidDto[],
  mayCreateContract: boolean,
): NowAction => {
  const contractIds = event.award?.contractIds ?? [];
  const winners = winningBids(event, bids);

  if (winners.length > 1) {
    const next = winners.find((bid) => !bid.contractId);
    return next && event.status === SourcingEventStatus.Awarded && mayCreateContract
      ? createContractFor(next)
      : seeTheAward;
  }

  if (contractIds.length === 0) {
    const onlyWinner =
      event.status === SourcingEventStatus.Awarded && (event.award?.winningBidIds ?? []).length === 1
        ? winners[0]
        : undefined;
    return onlyWinner && mayCreateContract ? createContractFor(onlyWinner) : seeTheAward;
  }

  // contractUrl routes on the contract's state, which only the bid carries.
  const onlyContract =
    contractIds.length === 1 ? bids.find((bid) => bid.contractId === contractIds[0]) : undefined;
  return onlyContract
    ? {
      labelKey: "Go to contract",
      target: { kind: "url", url: contractUrl(contractIds[0], onlyContract.contractState, event.id) },
    }
    : seeTheAward;
};

const contractsPerWinner = (
  event: SourcingEventDto,
  bids: ReceivedBidDto[],
): Pick<NowPanelState, "status" | "detail" | "progress"> | null => {
  const winners = winningBids(event, bids);
  const missing = winners.filter((bid) => !bid.contractId);
  if (winners.length < 2 || missing.length === 0) return null;
  const done = winners.length - missing.length;
  return {
    status: {
      key: "{{done}} of {{total}} winners have a contract",
      params: { done, total: winners.length },
    },
    detail: [{ key: "No contract yet: {{suppliers}}", params: { suppliers: missing.map((bid) => bid.ownerName).join(", ") } }],
    progress: { done, total: winners.length },
  };
};

// t('Start the service contract') t('Open the service contract')
// FOA § 13-4 g / § 13-3 d: the contract after a design contest is its own procurement, not a contract on the entry.
const designContestFollowUp = (event: SourcingEventDto): NowAction | null => {
  const ctx = event.eventContext.type === "Public" ? event.eventContext : null;
  if (!isDesignContest(ctx?.procedureType)) return null;
  if (ctx?.designContest?.leadsToServiceContract !== true) return seeTheAward;
  const followUpId = ctx.designContest.followUpEventId;
  if (followUpId) {
    return { labelKey: "Open the service contract", target: { kind: "url", url: urls.app.sourcingEvents.view.go(followUpId) } };
  }
  return { labelKey: "Start the service contract", target: { kind: "command", command: "startDesignContestFollowUp" } };
};

const awardConfirmed = (input: NowPanelInput): NowPanelState => {
  if (input.awardLettersSent === false) {
    return {
      key: "AWARD_LETTERS_PENDING",
      status: {
        key: eventIsDesignContest(input.event)
          ? "The letters to the participants have not been sent yet"
          : "The award letters have not been sent yet",
      },
      detail: [],
      progress: null,
      action: awardLettersAction(input),
    };
  }

  const contractAction = designContestFollowUp(input.event) ??
    awardConfirmedAction(input.event, input.bids, input.mayCreateContract);
  const secondaryContractAction = contractAction === seeTheAward ? null : contractAction;
  return (
    intentionNotice(input, secondaryContractAction) ??
    resultNotice(input, secondaryContractAction) ?? {
      key: "AWARD_CONFIRMED",
      status: { key: "The award is confirmed" },
      detail: [],
      progress: null,
      ...contractsPerWinner(input.event, input.bids),
      action: contractAction,
    }
  );
};

const sendIntentionNotice: NowAction = {
  labelKey: "Send the intention notice",
  icon: "send",
  target: { kind: "command", command: "sendIntentionNotice" },
};

const sendIntentionNoticeAgain: NowAction = { ...sendIntentionNotice, labelKey: "Send the intention notice again" };

const concludeWithoutIntentionNotice: NowAction = {
  labelKey: "Conclude the contract without an intention notice",
  target: { kind: "command", command: "concludeWithoutIntentionNotice" },
};

// LOA § 13 (2): after a published intention notice the contract is protected only once ten days,
// counted from the day after the notice date, have passed.
const intentionNotice = (input: NowPanelInput, secondary: NowAction | null): NowPanelState | null => {
  const { event } = input;
  if (event.status !== SourcingEventStatus.Awarded || event.award?.contractConcludedOn != null) return null;
  if (!offersIntentionNotice(event)) return null;
  const notice = event.award?.intentionNotice;
  const state = (key: NowStateKey, status: NowPanelState["status"], action: NowAction | null, alternatives: NowAction[] = []) => ({
    key,
    status,
    detail: [],
    progress: null,
    action,
    secondary,
    alternatives,
  });

  if (notice == null && latestIntentionNoticeFailed(event)) {
    return state(
      "INTENTION_NOTICE_REFUSED",
      { key: "Doffin refused the intention notice, so the contract is not protected yet" },
      sendIntentionNoticeAgain,
      [concludeWithoutIntentionNotice],
    );
  }
  if (notice == null) {
    return state(
      "INTENTION_NOTICE_OPTIONAL",
      { key: "Send an intention notice to protect the contract, or conclude it now" },
      sendIntentionNotice,
      [concludeWithoutIntentionNotice],
    );
  }
  if (notice.status !== PublicationStatus.Published || notice.contractFrom == null) {
    return state("INTENTION_NOTICE_SENT", { key: "The intention notice is sent – awaiting Doffin" }, null);
  }
  const contractFrom = moment(notice.contractFrom).startOf("day");
  if (!moment(input.now).isBefore(contractFrom)) return null;
  return state(
    "INTENTION_NOTICE_WAIT",
    {
      key: "The contract can be concluded from {{date}} ({{timeLeft}})",
      dayIso: notice.contractFrom,
      timeLeft: timeLeftUntil(contractFrom.toISOString(), moment(input.now)),
    },
    notice.noticeNumber
      ? { labelKey: "See on Doffin", target: { kind: "doffinNotice", externalId: notice.noticeNumber } }
      : null,
  );
};

const latestIntentionNoticeFailed = (event: SourcingEventDto): boolean => {
  if (event.eventContext.type !== "Public") return false;
  const latest = (event.eventContext.publications ?? [])
    .filter((publication) => publication.channel === PublicationChannel.Doffin && publication.eFormType === EFormType.Veat25)
    .at(-1);
  return latest?.status === PublicationStatus.Failed;
};

const announceResult: NowAction = {
  labelKey: "Announce on Doffin",
  icon: "send",
  target: { kind: "command", command: "announceResult" },
};

const declineNotice: NowAction = {
  labelKey: "Do not announce",
  target: { kind: "command", command: "declineResultNotice" },
};

const AWARD_FINAL_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Completed,
]);

const unmetResultNotice = (input: NowPanelInput): NowPanelState | null => {
  const state = resultNotice(input, null);
  return state?.key === "RESULT_NOTICE_SENT" || state?.key === "RESULT_NOTICE_PUBLISHED" ? null : state;
};

const resultNotice = (input: NowPanelInput, secondary: NowAction | null): NowPanelState | null => {
  if (!AWARD_FINAL_STATUSES.has(input.event.status)) return null;
  const step = resultNoticeStep(input.event, input.now);
  const state = (
    key: NowStateKey,
    status: NowPanelState["status"],
    action: NowAction | null,
    alternatives: NowAction[] = [],
  ): NowPanelState => ({
    key,
    status,
    detail: [],
    progress: null,
    action,
    secondary,
    alternatives,
  });

  switch (step.kind) {
    case "none":
      return null;
    case "askConcludedDate":
      return state(
        "RESULT_NOTICE_CONCLUDED_DATE",
        { key: "When was the contract concluded?" },
        { labelKey: "Enter the date", target: { kind: "command", command: "recordContractConcluded" } },
      );
    case "due":
      return state(
        "RESULT_NOTICE_DUE",
        {
          key: eventIsDesignContest(input.event)
            ? "The contest's result must be announced by {{date}} ({{timeLeft}})"
            : "The contract award must be announced by {{date}} ({{timeLeft}})",
          dayIso: step.deadline,
          timeLeft: step.timeLeft,
        },
        step.urgent ? { ...announceResult, tone: "danger" } : announceResult,
      );
    case "voluntary":
      return state(
        "RESULT_NOTICE_VOLUNTARY",
        { key: "You can announce the result on Doffin (voluntary)" },
        announceResult,
        [declineNotice],
      );
    case "sent":
      return state("RESULT_NOTICE_SENT", { key: "The result notice is sent – awaiting Doffin" }, null);
    case "published":
      return state(
        "RESULT_NOTICE_PUBLISHED",
        { key: "The result is announced on Doffin" },
        step.externalId
          ? { labelKey: "See on Doffin", target: { kind: "doffinNotice", externalId: step.externalId } }
          : null,
      );
  }
};
